<template>
  <i-dropdown
    @open="onOpen"
    :isOpen="item.showOptions"
    @update:isOpen="(o) => setFilterParamOptions({name: item.name, showOptions: o})"
  >
    <template v-slot:trigger="{toggle, attrs}">
      <i-chip
          @click="toggle"
          @close="() => disableFilterParam({name: item.name})"
          :filter="paramLabel"
          :filterLabelWidth="160"
          :noFilterLabel="LANG.all"
          v-bind="attrs">
          {{ item.label }}
      </i-chip>
    </template>

    <template v-slot:default="dropdown">
      <div class="dropdown-menu dropdown-body block" style="padding: 0;">
        <div class="pa2 pb1 clearfix">
          <button
            v-for="preset in presetNames"
            :key="preset"
            :ref="(el) => presetRefs[preset] = el"
            type="button"
            class="date_filter_chip"
            :class="{'active': preset === activePreset}"
            @click="() => setDatePreset(preset)">{{ LANG[preset] }}</button>
        </div>
        <div v-if="startDate" class="pa2">
            <label>{{ LANG.startDateFrom }}</label>
            <i-date-select mode="dateTime" v-model="startDate" @keydown.stop />
        </div>
        <div v-if="endDate" class="pa2">
            <label>{{ LANG.endDateTill }}</label>
            <i-date-select mode="dateTime" v-model="endDate" @keydown.stop />
        </div>
        <div class="i-css-right pa2">
          <button
            @click.prevent.stop="() => clear(dropdown)"
            @keydown.enter.stop="() => clear(dropdown)"
            :title="LANG.clear"
            class="btn i-button i-secondary-btn i-filter-clear" >{{ LANG.clear }}</button>
          <button
            @click="() => apply(dropdown)"
            @keydown.enter.stop="() => apply(dropdown)"
            :title="LANG.apply"
            class="btn i-button i-filter-update" >{{ LANG.apply }}</button>
        </div>
      </div>
    </template>
  </i-dropdown>
</template>

<script>
import { mapState } from 'vuex';
import { mapBrowserMutations } from '@/store';
import { dateParse, dateFormat } from '@/store/modules/entity_browser/iql-builder';

const morning = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
const evening = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
const lastDaysRange = (days) => () => {
  const today = new Date();
  const daysAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days + 1);
  return [morning(daysAgo), evening(today)];
};

const formatDate = (date) => date.toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' });

export default {
  props: {
    item: Object,
  },
  data() {
    return {
      startDate: null,
      endDate: null,
      activePreset: '',
      presets: {
        today: () => {
          const today = new Date();
          return [morning(today), evening(today)];
        },
        yesterday: () => {
          const today = new Date();
          const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          return [morning(yesterday), evening(yesterday)];
        },
        last3days: lastDaysRange(3),
        last7days: lastDaysRange(7),
        last30days: lastDaysRange(30),
        inThePast: () => [null, new Date()],
        customDates: () => [this.startDate ?? new Date(), this.endDate ?? new Date()],
      },
      presetRefs: {},
      showValidation: true,
    };
  },
  methods: {
    ...mapBrowserMutations([
      'disableFilterParam',
      'setFilterParam',
      'setFilterParamOptions',
    ]),
    setDates(startDate, endDate) {
      this.startDate = startDate;
      this.endDate = endDate;
    },
    setDatePreset(name) {
      const preset = this.presets[name];
      if (!preset) return;

      this.activePreset = name;
      this.setDates(...preset());
    },
    apply(dropdown) {
      const { startDate, endDate } = this;
      this.params = [startDate ?? null, endDate ?? null];

      if (dropdown) {
        dropdown.close();
      }
    },
    clear(dropdown) {
      this.setDates(null, null);
      this.activePreset = '';
      this.apply(dropdown);
    },
    onOpen() {
      if (this.hasParams) {
        this.setDates(...this.params);
      } else {
        this.setDatePreset('last3days');
      }
      const presetRef = this.presetRefs[this.activePreset ?? 'today'];
      if (presetRef) {
        presetRef.focus();
      }
    },
  },
  computed: {
    ...mapState(['LANG']),
    params: {
      // NOTE:
      // the dates are serialized as primitive values(strings) in the app state
      // to allow persistence
      // So this getter/setter acts as a serialization mechanism
      // when setting new dates as filter params
      // we first convert the internal Date objects to strings
      // and when we read the values from state
      // we convert the strings to Date objects for internal usage
      get() {
        return this.item.value.map(dateParse);
      },
      set(dates) {
        this.setFilterParam({
          name: this.item.name,
          value: dates.map(dateFormat),
        });
      },
    },
    hasParams() {
      return [...this.params].filter(Boolean).length;
    },
    presetNames() {
      return Object.keys(this.presets);
    },
    paramLabel() {
      // eslint-disable-next-line no-confusing-arrow
      const [startLabel, endLabel] = this.params.map((d) => d ? formatDate(d) : d);
      if (!startLabel && endLabel) {
        return `${this.LANG.before} ${endLabel}`;
      }
      if (startLabel && !endLabel) {
        return `${this.LANG.after} ${startLabel}`;
      }

      return [...new Set([startLabel, endLabel])]
        .filter(Boolean)
        .join(' — ');
    },
  },
};
</script>
